import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LogOut, BarChart3, Users, UserCog, Flag, Receipt, Ban, ShieldCheck, ShieldX, Settings, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { divinationLabel } from '@/data/divination';
import { reqNo } from '@/data/format';
import { symbolOf } from '@/data/money';

const REASON_LABEL = {
  scam: '詐騙',
  fake: '虛假',
  harassment: '騷擾',
  spam: '垃圾訊息',
  off_topic: '答非所問',
  other: '其他'
};

const TARGET_LABEL = { user: '用戶', message: '訊息', request: '求測單' };

const ROLE_LABEL = { admin: '管理員', astrologer: '命理師', client: '求測者' };

const STATUS_LABEL = { pending: '待處理', actioned: '已處理', dismissed: '已駁回' };

const STATUS_CLASS = {
  pending: 'bg-[#FEF8F0] text-[#C93B2B]',
  actioned: 'bg-[#E5E0D8] text-[#1A1A1A]',
  dismissed: 'bg-[#F5F0E6] text-[#5C5C5C]'
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [reports, setReports] = useState([]);
  const [orders, setOrders] = useState([]);
  const [banDialog, setBanDialog] = useState(null);   // {user_id, name}
  const [actionDialog, setActionDialog] = useState(null); // report
  const [banForm, setBanForm] = useState({ duration: '7', reason: '' });
  const [actionForm, setActionForm] = useState({ action: 'dismiss', duration: '7', reason: '' });
  const [expiryDays, setExpiryDays] = useState('30');
  const [freeMode, setFreeMode] = useState(true);
  const [maxPicks, setMaxPicks] = useState('1');
  const [postPickDays, setPostPickDays] = useState('7');
  const [deduping, setDeduping] = useState(false);
  const [requests, setRequests] = useState([]);
  const [retrying, setRetrying] = useState(false);
  const [userQ, setUserQ] = useState('');
  const [userSkip, setUserSkip] = useState(0);
  const [bcMsg, setBcMsg] = useState('');
  const [bcAudience, setBcAudience] = useState('all');
  const [bcSending, setBcSending] = useState(false);
  const USER_PAGE = 100;

  const fetchAll = useCallback(async () => {
    try {
      const [s, u, p, r, o, st, rq] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/profiles`, { withCredentials: true }),
        axios.get(`${API}/admin/reports`, { withCredentials: true }),
        axios.get(`${API}/admin/orders`, { withCredentials: true }),
        axios.get(`${API}/admin/settings`, { withCredentials: true }),
        axios.get(`${API}/admin/requests`, { withCredentials: true })
      ]);
      setStats(s.data);
      setUsers(u.data);
      setProfiles(p.data);
      setReports(r.data);
      setOrders(o.data);
      setRequests(rq.data);
      setExpiryDays(String(st.data.request_expiry_days ?? 30));
      setFreeMode(st.data.free_mode ?? true);
      setMaxPicks(String(st.data.max_picks_per_request ?? 1));
      setPostPickDays(String(st.data.post_pick_days ?? 7));
    } catch (err) {
      console.error(err);
      toast.error('無法載入管理員資料');
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const submitBan = async () => {
    if (!banDialog) return;
    try {
      const duration_days = banForm.duration === 'permanent' ? null : Number(banForm.duration);
      await axios.post(
        `${API}/admin/users/${banDialog.user_id}/ban`,
        { duration_days, reason: banForm.reason || '違反平台規則' },
        { withCredentials: true }
      );
      toast.success('已封禁');
      setBanDialog(null);
      setBanForm({ duration: '7', reason: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || '封禁失敗');
    }
  };

  const unbanUser = async (user_id) => {
    try {
      await axios.post(`${API}/admin/users/${user_id}/unban`, {}, { withCredentials: true });
      toast.success('已解封');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || '操作失敗');
    }
  };

  const searchUsers = async (q, skip = 0) => {
    try {
      const { data } = await axios.get(`${API}/admin/users`, {
        params: { q: q || undefined, skip, limit: USER_PAGE },
        withCredentials: true,
      });
      setUsers(data);
      setUserSkip(skip);
    } catch (err) {
      toast.error('搜尋失敗');
    }
  };

  const viewConversation = async (messageId) => {
    try {
      const { data } = await axios.get(`${API}/admin/messages/${messageId}`, { withCredentials: true });
      navigate(`/chat/${data.order_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || '搵唔到對話');
    }
  };

  const sendBroadcast = async () => {
    const msg = bcMsg.trim();
    if (!msg) { toast.error('請輸入公告內容'); return; }
    if (!window.confirm(`確定向「${bcAudience === 'all' ? '全部用戶' : bcAudience === 'client' ? '所有求測者' : '所有命理師'}」發送公告？`)) return;
    setBcSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/broadcast`, { message: msg, audience: bcAudience }, { withCredentials: true });
      toast.success(`已發送畀 ${data.sent} 位用戶`);
      setBcMsg('');
    } catch (err) {
      toast.error(err.response?.data?.detail || '發送失敗');
    } finally {
      setBcSending(false);
    }
  };

  const retryScoring = async () => {
    setRetrying(true);
    try {
      const { data } = await axios.post(`${API}/admin/scoring/retry`, {}, { withCredentials: true });
      toast.success(`已安排 ${data.retried} 個回答重新評分，完成後會更新`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || '重試失敗');
    } finally {
      setRetrying(false);
    }
  };

  const dedupeUsers = async () => {
    if (!window.confirm('清理重複帳戶：同一 email 下，保留有資料嗰個，刪走「完全冇資料」嘅多餘帳戶，並重建 email 唯一索引。繼續？')) return;
    setDeduping(true);
    try {
      const { data } = await axios.post(`${API}/admin/users/dedupe`, {}, { withCredentials: true });
      const r = data.removed?.length || 0;
      const c = data.conflicts?.length || 0;
      toast.success(`已刪 ${r} 個重複空白帳戶；索引：${data.index}${c ? `；${c} 組兩邊都有資料，需人手處理（見 console）` : ''}`);
      if (c) console.warn('Dedupe conflicts (manual merge needed):', data.conflicts);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || '清理失敗');
    } finally {
      setDeduping(false);
    }
  };

  const toggleProfile = async (user_id, currentStatus) => {
    try {
      const endpoint = currentStatus === 'disabled' ? 'enable' : 'disable';
      await axios.post(
        `${API}/admin/profiles/${user_id}/${endpoint}`,
        { reason: '管理員操作', duration_days: null },
        { withCredentials: true }
      );
      toast.success(endpoint === 'enable' ? '已恢復個人頁' : '已停用個人頁');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || '操作失敗');
    }
  };

  const submitReportAction = async () => {
    if (!actionDialog) return;
    try {
      const body = { action: actionForm.action, reason: actionForm.reason || undefined };
      if (actionForm.action === 'ban_target') {
        body.duration_days = actionForm.duration === 'permanent' ? null : Number(actionForm.duration);
      }
      await axios.post(`${API}/admin/reports/${actionDialog.report_id}/action`, body, { withCredentials: true });
      toast.success('已處理舉報');
      setActionDialog(null);
      setActionForm({ action: 'dismiss', duration: '7', reason: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || '操作失敗');
    }
  };

  const saveSettings = async () => {
    const n = Number(expiryDays);
    if (!n || n < 1 || n > 365) {
      toast.error('過期日數需介乎 1 至 365');
      return;
    }
    const mp = Number(maxPicks);
    if (!Number.isInteger(mp) || mp < 1 || mp > 5) {
      toast.error('每單可選命理師數需介乎 1 至 5');
      return;
    }
    const ppd = Number(postPickDays);
    if (!Number.isInteger(ppd) || ppd < 1 || ppd > 90) {
      toast.error('揀後自動完成日數需介乎 1 至 90');
      return;
    }
    try {
      await axios.put(`${API}/admin/settings`, { request_expiry_days: n, free_mode: freeMode, max_picks_per_request: mp, post_pick_days: ppd }, { withCredentials: true });
      toast.success('已儲存設定');
    } catch (err) {
      toast.error(err.response?.data?.detail || '儲存失敗');
    }
  };

  // A ban is only "active" until its expiry — an expired banned_until means free again.
  const isBanActive = (banned_until) => !!banned_until && new Date(banned_until) > new Date();

  const banLabel = (banned_until) => {
    if (!isBanActive(banned_until)) return null;
    const dt = new Date(banned_until);
    if (dt.getFullYear() >= 9999) return '永久封禁';
    return `封禁至 ${dt.toLocaleDateString('zh-TW')}`;
  };

  const renderUserAction = (u) => {
    if (u.role === 'admin') {
      return <span className="text-xs text-[#5C5C5C]">—</span>;
    }
    if (isBanActive(u.banned_until)) {
      return (
        <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => unbanUser(u.user_id)} data-testid={`unban-${u.user_id}`}>
          <ShieldCheck className="w-3 h-3 mr-1" strokeWidth={1.5} />解封
        </Button>
      );
    }
    return (
      <Button size="sm" variant="outline" className="rounded-sm text-xs border-[#C93B2B] text-[#C93B2B]" onClick={() => setBanDialog({ user_id: u.user_id, name: u.name })} data-testid={`ban-${u.user_id}`}>
        <Ban className="w-3 h-3 mr-1" strokeWidth={1.5} />封禁
      </Button>
    );
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-[#E5E0D8] px-6 md:px-12 lg:px-24 py-4 flex justify-between items-center">
        <Link to="/admin/dashboard">
          <h2 className="text-xl font-serif text-[#C93B2B]">命理配對平台 · 管理後台</h2>
        </Link>
        <div className="flex items-center gap-4">
          <span className="text-sm text-[#5C5C5C]">{user?.name}（管理員）</span>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="rounded-sm" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
            登出
          </Button>
        </div>
      </nav>

      <div className="px-6 md:px-12 lg:px-24 py-10">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#1A1A1A] mb-2">管理後台</h1>
        <p className="text-base text-[#5C5C5C] mb-8">監控平台運作、處理舉報、管理用戶</p>

        <Tabs defaultValue="stats" className="w-full">
          <TabsList className="grid w-full grid-cols-7 rounded-sm mb-6">
            <TabsTrigger value="stats" data-testid="tab-stats"><BarChart3 className="w-4 h-4 mr-2" strokeWidth={1.5} />統計</TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests"><FileText className="w-4 h-4 mr-2" strokeWidth={1.5} />求測單</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users"><Users className="w-4 h-4 mr-2" strokeWidth={1.5} />求測者</TabsTrigger>
            <TabsTrigger value="profiles" data-testid="tab-profiles"><UserCog className="w-4 h-4 mr-2" strokeWidth={1.5} />命理師</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">
              <Flag className="w-4 h-4 mr-2" strokeWidth={1.5} />
              舉報{stats?.pending_reports > 0 && <span className="ml-1 px-1.5 py-0.5 bg-[#C93B2B] text-white text-xs rounded-sm">{stats.pending_reports}</span>}
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders"><Receipt className="w-4 h-4 mr-2" strokeWidth={1.5} />訂單</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings"><Settings className="w-4 h-4 mr-2" strokeWidth={1.5} />設定</TabsTrigger>
          </TabsList>

          {/* STATS */}
          <TabsContent value="stats">
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-grid">
                {[
                  ['總用戶數', stats.total_users],
                  ['求測者', stats.total_clients],
                  ['命理師', stats.total_astrologers],
                  ['求測單', stats.total_requests],
                  ['開放中', stats.open_requests],
                  ['已完成', stats.completed_requests],
                  ['已過期', stats.expired_requests],
                  ['訂單', stats.total_orders],
                  [freeMode ? '配對成交額（免費期，未收款）' : '總收入',
                    (stats.revenue_by_currency || []).length
                      ? (stats.revenue_by_currency || []).map((r) => `${symbolOf(r.currency)} ${Number(r.total).toLocaleString()}`).join(' · ')
                      : '0'],
                  ['待處理舉報', stats.pending_reports],
                  ['封禁用戶', stats.banned_users]
                ].map(([label, val]) => (
                  <div key={label} className="bg-white border border-[#E5E0D8] rounded-sm p-5">
                    <p className="text-xs text-[#5C5C5C] mb-1">{label}</p>
                    <p className="text-2xl font-serif text-[#1A1A1A]">{val}</p>
                  </div>
                ))}
              </div>
            )}
            {stats && (
              <div className={`mt-4 border rounded-sm p-5 flex items-center justify-between gap-4 ${stats.scoring_failed > 0 ? 'bg-[#FBEAE8] border-[#C93B2B]' : 'bg-white border-[#E5E0D8]'}`} data-testid="scoring-health">
                <div>
                  <p className="text-xs text-[#5C5C5C] mb-1">評分健康</p>
                  <p className={`text-2xl font-serif ${stats.scoring_failed > 0 ? 'text-[#C93B2B]' : 'text-[#1E7E34]'}`}>
                    {stats.scoring_failed > 0 ? `${stats.scoring_failed} 個答案評分失敗` : '正常'}
                  </p>
                  {stats.scoring_failed > 0 && <p className="text-xs text-[#C93B2B] mt-1">評分曾失敗（如評分服務 key 過期／額度用盡），呢啲答案暫時冇準度。</p>}
                </div>
                {stats.scoring_failed > 0 && (
                  <Button onClick={retryScoring} disabled={retrying} variant="outline" className="border-[#C93B2B] text-[#C93B2B] hover:bg-white rounded-sm shrink-0" data-testid="retry-scoring-btn">
                    {retrying ? '重試中…' : '重試評分'}
                  </Button>
                )}
              </div>
            )}
          </TabsContent>

          {/* REQUESTS */}
          <TabsContent value="requests">
            <p className="text-sm text-[#5C5C5C] mb-3">按「回答最少、最快過期」排序 —— 零回答（供應缺口）嘅單浮上頂，橙色標示。</p>
            <div className="bg-white border border-[#E5E0D8] rounded-sm overflow-x-auto" data-testid="requests-table">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F0E6] text-[#5C5C5C]">
                  <tr>
                    <th className="text-left p-3">#</th>
                    <th className="text-left p-3">求測者</th>
                    <th className="text-left p-3">類型</th>
                    <th className="text-left p-3">狀態</th>
                    <th className="text-left p-3">回答／已選</th>
                    <th className="text-left p-3">剩餘</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr key={r.request_id} className={`border-t border-[#E5E0D8] ${r.status === 'open' && r.answer_count === 0 ? 'bg-[#FBF3E8]' : ''}`} data-testid={`admin-req-${r.request_id}`}>
                      <td className="p-3 font-mono text-[#9A8A6A]">{reqNo(r.seq)}</td>
                      <td className="p-3 text-[#1A1A1A]">{r.client_name}</td>
                      <td className="p-3 text-[#5C5C5C]">{divinationLabel(r)}</td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${r.status === 'open' ? 'bg-[#F5F0E6] text-[#C93B2B]' : r.status === 'expired' ? 'bg-[#E5E0D8] text-[#9A6A2B]' : 'bg-[#E5E0D8] text-[#1A1A1A]'}`}>
                          {r.status === 'open' ? '開放中' : r.status === 'expired' ? '已過期' : '已完成'}
                        </span>
                      </td>
                      <td className="p-3 text-[#5C5C5C]">{r.answer_count} 答 · {r.order_count} 選</td>
                      <td className="p-3 text-[#5C5C5C]">{r.status === 'open' ? `${r.days_left ?? '—'} 日` : '—'}</td>
                    </tr>
                  ))}
                  {requests.length === 0 && (
                    <tr><td colSpan="6" className="p-6 text-center text-[#9A9A9A]">未有求測單</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* USERS */}
          <TabsContent value="users">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Input
                value={userQ}
                onChange={(e) => setUserQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') searchUsers(userQ, 0); }}
                placeholder="搜尋 email 或姓名…"
                className="rounded-sm w-64"
                data-testid="user-search-input"
              />
              <Button variant="outline" className="rounded-sm" onClick={() => searchUsers(userQ, 0)} data-testid="user-search-btn">搜尋</Button>
              {userSkip > 0 && (
                <Button variant="outline" className="rounded-sm text-xs" onClick={() => searchUsers(userQ, Math.max(0, userSkip - USER_PAGE))}>上一頁</Button>
              )}
              {users.length >= USER_PAGE && (
                <Button variant="outline" className="rounded-sm text-xs" onClick={() => searchUsers(userQ, userSkip + USER_PAGE)}>下一頁</Button>
              )}
            </div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-[#5C5C5C]">同一 email 應只得一個帳戶。如見重複，可一鍵清理空白重複戶。</p>
              <Button
                onClick={dedupeUsers}
                disabled={deduping}
                variant="outline"
                className="border-[#C93B2B] text-[#C93B2B] hover:bg-[#FBEAE8] rounded-sm"
                data-testid="dedupe-users-btn"
              >
                {deduping ? '清理中…' : '清理重複帳戶'}
              </Button>
            </div>
            <div className="bg-white border border-[#E5E0D8] rounded-sm overflow-x-auto" data-testid="users-table">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F0E6] text-[#5C5C5C]">
                  <tr>
                    <th className="text-left p-3">姓名</th>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">角色</th>
                    <th className="text-left p-3">狀態</th>
                    <th className="text-left p-3">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {users.filter((u) => u.role === 'client').map((u) => (
                    <tr key={u.user_id} className="border-t border-[#E5E0D8]" data-testid={`user-row-${u.user_id}`}>
                      <td className="p-3 text-[#1A1A1A]">{u.name}</td>
                      <td className="p-3 text-[#5C5C5C]">{u.email}</td>
                      <td className="p-3">
                        <span className="inline-flex items-center border border-[#C93B2B] text-[#C93B2B] px-2 py-0.5 text-xs rounded-none">
                          {ROLE_LABEL[u.role] || u.role}
                        </span>
                      </td>
                      <td className="p-3">
                        {isBanActive(u.banned_until) ? (
                          <span className="text-[#C93B2B] text-xs">{banLabel(u.banned_until)}</span>
                        ) : (
                          <span className="text-[#5C5C5C] text-xs">正常</span>
                        )}
                      </td>
                      <td className="p-3">
                        {renderUserAction(u)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* PROFILES */}
          <TabsContent value="profiles">
            <div className="bg-white border border-[#E5E0D8] rounded-sm overflow-x-auto" data-testid="profiles-table">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F0E6] text-[#5C5C5C]">
                  <tr>
                    <th className="text-left p-3">顯示名稱</th>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">地區</th>
                    <th className="text-left p-3">狀態</th>
                    <th className="text-left p-3">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {profiles.map((p) => (
                    <tr key={p.user_id} className="border-t border-[#E5E0D8]" data-testid={`profile-row-${p.user_id}`}>
                      <td className="p-3 text-[#1A1A1A]">{p.display_name}</td>
                      <td className="p-3 text-[#5C5C5C]">{p.email}</td>
                      <td className="p-3 text-[#5C5C5C]">{p.region}</td>
                      <td className="p-3">
                        <div className="flex flex-col gap-0.5 text-xs">
                          <span className={p.profile_status === 'disabled' ? 'text-[#C93B2B]' : 'text-[#5C5C5C]'}>
                            個人頁：{p.profile_status === 'disabled' ? '已停用' : '正常'}
                          </span>
                          {isBanActive(p.banned_until) && <span className="text-[#C93B2B]">{banLabel(p.banned_until)}</span>}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => toggleProfile(p.user_id, p.profile_status)} data-testid={`toggle-profile-${p.user_id}`}>
                            {p.profile_status === 'disabled' ? <><ShieldCheck className="w-3 h-3 mr-1" strokeWidth={1.5} />恢復</> : <><ShieldX className="w-3 h-3 mr-1" strokeWidth={1.5} />停用</>}
                          </Button>
                          {renderUserAction({ user_id: p.user_id, name: p.display_name, role: 'astrologer', banned_until: p.banned_until })}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* REPORTS */}
          <TabsContent value="reports">
            <div className="space-y-4" data-testid="reports-list">
              {reports.length === 0 && (
                <div className="bg-white border border-[#E5E0D8] rounded-sm p-8 text-center text-sm text-[#5C5C5C]">尚無舉報</div>
              )}
              {reports.map((r) => (
                <div key={r.report_id} className="bg-white border border-[#E5E0D8] rounded-sm p-5" data-testid={`report-${r.report_id}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="text-sm">
                        <span className="text-[#C93B2B] font-medium">{REASON_LABEL[r.reason] || r.reason}</span>
                        <span className="text-[#5C5C5C]"> · {TARGET_LABEL[r.target_type] || r.target_type}</span>
                      </p>
                      <p className="text-xs text-[#5C5C5C] mt-1">
                        舉報者：{r.reporter_name} · {new Date(r.created_at).toLocaleString('zh-TW')}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-sm ${STATUS_CLASS[r.status] || STATUS_CLASS.dismissed}`}>
                      {STATUS_LABEL[r.status] || r.status}
                    </span>
                  </div>
                  <div className="text-sm text-[#1A1A1A] mb-2">
                    對象：{r.target_user_name || '未知'}（{ROLE_LABEL[r.target_user_role] || r.target_user_role}）
                  </div>
                  {r.message_content && (
                    <div className="bg-[#F5F0E6] rounded-sm p-3 text-sm text-[#1A1A1A] mb-2">
                      <p className="text-xs text-[#5C5C5C] mb-1">舉報訊息內容：</p>
                      {r.message_content}
                    </div>
                  )}
                  {r.target_type === 'message' && (
                    <Button size="sm" variant="outline" className="rounded-sm text-xs mb-2" onClick={() => viewConversation(r.target_id)} data-testid={`view-conv-${r.report_id}`}>
                      查看完整對話
                    </Button>
                  )}
                  {r.description && <p className="text-sm text-[#5C5C5C] mb-2">說明：{r.description}</p>}
                  {r.action_taken && <p className="text-xs text-[#5C5C5C]">處理：{r.action_taken}</p>}
                  {r.status === 'pending' && (
                    <Button size="sm" className="bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm mt-3" onClick={() => setActionDialog(r)} data-testid={`action-${r.report_id}`}>
                      處理舉報
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ORDERS */}
          <TabsContent value="orders">
            <div className="bg-white border border-[#E5E0D8] rounded-sm overflow-x-auto" data-testid="orders-table">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F0E6] text-[#5C5C5C]">
                  <tr>
                    <th className="text-left p-3">訂單編號</th>
                    <th className="text-left p-3">求測者</th>
                    <th className="text-left p-3">命理師</th>
                    <th className="text-left p-3">金額</th>
                    <th className="text-left p-3">付款時間</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.order_id} className="border-t border-[#E5E0D8]">
                      <td className="p-3 text-[#5C5C5C] text-xs">{o.order_id}</td>
                      <td className="p-3 text-[#1A1A1A]">{o.client_name}</td>
                      <td className="p-3 text-[#1A1A1A]">{o.astrologer_name}</td>
                      <td className="p-3 text-[#C93B2B]">{symbolOf(o.currency)} {o.amount.toLocaleString()}</td>
                      <td className="p-3 text-[#5C5C5C] text-xs">{new Date(o.paid_at).toLocaleString('zh-TW')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* SETTINGS */}
          <TabsContent value="settings">
            <div className="bg-white border border-[#E5E0D8] rounded-sm p-6 max-w-md" data-testid="settings-panel">
              <h3 className="text-lg font-serif text-[#1A1A1A] mb-1">求測單設定</h3>
              <p className="text-sm text-[#5C5C5C] mb-4">
                超過此日數仍未成交的求測單會自動過期：命理師不再看到、不能回答；求測者仍可選擇已收到的回答。
              </p>
              <Label htmlFor="expiry-days">求測單過期日數</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  id="expiry-days"
                  type="number"
                  min="1"
                  max="365"
                  value={expiryDays}
                  onChange={(e) => setExpiryDays(e.target.value)}
                  className="rounded-sm w-32"
                  data-testid="expiry-days-input"
                />
                <span className="text-sm text-[#5C5C5C]">日</span>
              </div>

              <div className="mt-6 pt-4 border-t border-[#E5E0D8]">
                <Label htmlFor="max-picks">每張求測單可選命理師數</Label>
                <p className="text-sm text-[#5C5C5C] mb-2 mt-1">
                  求測者每張單最多可選幾多位命理師。設為 1：揀一位即完成；大於 1：揀第一位後 7 日內未選滿會自動完成。
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    id="max-picks"
                    type="number"
                    min="1"
                    max="5"
                    value={maxPicks}
                    onChange={(e) => setMaxPicks(e.target.value)}
                    className="rounded-sm w-32"
                    data-testid="max-picks-input"
                  />
                  <span className="text-sm text-[#5C5C5C]">位</span>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-[#E5E0D8]">
                <Label htmlFor="post-pick-days">揀後自動完成日數</Label>
                <p className="text-sm text-[#5C5C5C] mb-2 mt-1">
                  當每單可選多於 1 位時：求測者揀第一位後，若此日數內未選滿，求測單會自動完成（不會超過原本過期日）。
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    id="post-pick-days"
                    type="number"
                    min="1"
                    max="90"
                    value={postPickDays}
                    onChange={(e) => setPostPickDays(e.target.value)}
                    className="rounded-sm w-32"
                    data-testid="post-pick-days-input"
                  />
                  <span className="text-sm text-[#5C5C5C]">日</span>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-[#E5E0D8]">
                <h3 className="text-lg font-serif text-[#1A1A1A] mb-1">平台收費模式</h3>
                <p className="text-sm text-[#5C5C5C] mb-3">
                  平台免費開通諮詢室；命理師服務費由雙方自行商議。付款功能尚未啟用，暫時不可切換至收費模式。
                </p>
                <button
                  type="button"
                  onClick={() => setFreeMode(true)}
                  disabled={freeMode}
                  className={`px-4 py-2 rounded-sm border text-sm transition-colors ${freeMode ? 'bg-[#C93B2B] text-white border-[#C93B2B]' : 'bg-white text-[#1A1A1A] border-[#E5E0D8] hover:bg-[#F5F0E6]'}`}
                  data-testid="free-mode-toggle"
                >
                  {freeMode ? '平台免費開通：開啟中' : '恢復免費模式（儲存後生效）'}
                </button>
              </div>

              <Button onClick={saveSettings} className="bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm mt-6" data-testid="save-settings-btn">
                儲存
              </Button>

              <div className="mt-8 pt-4 border-t border-[#E5E0D8]">
                <h3 className="text-lg font-serif text-[#1A1A1A] mb-1">發送公告</h3>
                <p className="text-sm text-[#5C5C5C] mb-3">
                  以站內通知形式發送畀所有用戶（或其中一種身份）。最多 200 字。
                </p>
                <Textarea
                  value={bcMsg}
                  onChange={(e) => setBcMsg(e.target.value)}
                  placeholder="例：平台將於本週日凌晨進行維護…"
                  className="rounded-sm mb-2"
                  rows={3}
                  maxLength={200}
                  data-testid="broadcast-input"
                />
                <div className="flex items-center gap-2">
                  <Select value={bcAudience} onValueChange={setBcAudience}>
                    <SelectTrigger className="rounded-sm w-40" data-testid="broadcast-audience"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">全部用戶</SelectItem>
                      <SelectItem value="client">只限求測者</SelectItem>
                      <SelectItem value="astrologer">只限命理師</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={sendBroadcast} disabled={bcSending} variant="outline" className="border-[#C93B2B] text-[#C93B2B] hover:bg-[#FBEAE8] rounded-sm" data-testid="broadcast-send-btn">
                    {bcSending ? '發送中…' : '發送公告'}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* BAN DIALOG */}
      <Dialog open={!!banDialog} onOpenChange={(o) => !o && setBanDialog(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-serif">封禁用戶</DialogTitle>
            <DialogDescription>{banDialog?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>封禁時長</Label>
              <Select value={banForm.duration} onValueChange={(v) => setBanForm({ ...banForm, duration: v })}>
                <SelectTrigger className="rounded-sm mt-2" data-testid="ban-duration"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 日</SelectItem>
                  <SelectItem value="30">30 日</SelectItem>
                  <SelectItem value="90">90 日</SelectItem>
                  <SelectItem value="permanent">永久</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>原因</Label>
              <Textarea value={banForm.reason} onChange={(e) => setBanForm({ ...banForm, reason: e.target.value })} className="rounded-sm mt-2" placeholder="例如：多次散播詐騙訊息" data-testid="ban-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBanDialog(null)} className="rounded-sm">取消</Button>
            <Button onClick={submitBan} className="bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm" data-testid="confirm-ban">確認封禁</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REPORT ACTION DIALOG */}
      <Dialog open={!!actionDialog} onOpenChange={(o) => !o && setActionDialog(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-serif">處理舉報</DialogTitle>
            <DialogDescription>
              {actionDialog && `${REASON_LABEL[actionDialog.reason]} · ${TARGET_LABEL[actionDialog.target_type]} · 對象：${actionDialog.target_user_name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>處理動作</Label>
              <Select value={actionForm.action} onValueChange={(v) => setActionForm({ ...actionForm, action: v })}>
                <SelectTrigger className="rounded-sm mt-2" data-testid="action-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dismiss">駁回（無事實依據）</SelectItem>
                  <SelectItem value="ban_target">封禁被舉報者</SelectItem>
                  <SelectItem value="disable_profile">停用其命理師個人頁</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {actionForm.action === 'ban_target' && (
              <div>
                <Label>封禁時長</Label>
                <Select value={actionForm.duration} onValueChange={(v) => setActionForm({ ...actionForm, duration: v })}>
                  <SelectTrigger className="rounded-sm mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 日</SelectItem>
                    <SelectItem value="30">30 日</SelectItem>
                    <SelectItem value="90">90 日</SelectItem>
                    <SelectItem value="permanent">永久</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>備註（可選）</Label>
              <Textarea value={actionForm.reason} onChange={(e) => setActionForm({ ...actionForm, reason: e.target.value })} className="rounded-sm mt-2" placeholder="處理理由" data-testid="action-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)} className="rounded-sm">取消</Button>
            <Button onClick={submitReportAction} className="bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm" data-testid="confirm-action">確認處理</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
